import * as Yup from 'yup';
import * as React from 'react';
import { Formik } from 'formik';
import { Button } from 'react-covideo-common';
import { useParams } from 'react-router';
//components
import { TextField } from 'components/formik/TextField';
import { TextAreaField } from 'components/formik/TextAreaField';
import { Gap } from 'components/layout';
import { NewModal } from 'components/NewModal';
import { UploadLoader } from 'components/record/UploadLoader';
//hooks
import { useVideoRecordUpload } from './useVideoRecordUpload';
import { useTranslation } from 'react-i18next';

export const FILE_EXTENSION = 'webm';

const generateName = (fileExt: string) => {
  const randomSuffix = (Math.random() + 1).toString(36).substring(2);
  const fileName = `${randomSuffix}.${fileExt}`;
  return fileName;
};

type Props = {
  handleModalClose: () => void;
  videoBlob: Blob;
};

export interface IUploadVideoFormikValues {
  email: string;
  name: string;
  message: string;
}

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .matches(
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      'Invalid email format'
    )
    .required('Email is required'),
});

export const ModalUploadRecordVideo = ({
  handleModalClose = () => {},
  videoBlob,
}: Props) => {
  const { t } = useTranslation();
  const { shortUrl, eridEncoded } = useParams() as {
    shortUrl: string;
    eridEncoded: string;
  };

  const {
    createVideoReplySignedUrl,
    setRecordingFormData,
    isLoading: isSubmittingForm,
    uploadToS3Progress,
    createProgress,
    progressState,
  } = useVideoRecordUpload({ handleModalClose });

  const INITIAL_VALUES = { name: '', email: '', message: '' };
  const onSubmitHandler = (values: IUploadVideoFormikValues) => {
    const fileName = generateName(FILE_EXTENSION);
    const videoFile = new File([videoBlob], fileName, { type: videoBlob.type });
    setRecordingFormData({ ...values, file: videoFile });

    createVideoReplySignedUrl({
      filename: fileName,
      filetype: videoBlob.type,
      eridEncoded,
      shortUrl,
    });
  };

  return (
    <NewModal
      headerText={t('Enter your information')}
      closeModal={handleModalClose}
      style={{
        content: {
          maxWidth: '592px',
        },
      }}
      hideHeader={isSubmittingForm}
    >
      {isSubmittingForm ? (
        <UploadLoader
          progressState={progressState}
          uploadToS3Progress={uploadToS3Progress}
          createProgress={createProgress}
        />
      ) : (
        <Gap m='16px 0 0 0' width='100%' justifyContent='flex-end'>
          <Formik
            initialValues={INITIAL_VALUES}
            onSubmit={onSubmitHandler}
            validationSchema={validationSchema}
          >
            {({ submitForm, isValid }) => (
              <>
                <TextField name='name' label={t('Name')} type='text' />
                <TextField name='email' label={t('Email')} type='text' />
                <TextAreaField name='message' label={t('Message')} rows={6} />

                <Button
                  text={t('Cancel')}
                  onClick={handleModalClose}
                  disabled={!isValid}
                  variant='destructive'
                />
                <Button
                  type='submit'
                  text={t('Send')}
                  onClick={submitForm}
                  disabled={!isValid}
                />
              </>
            )}
          </Formik>
        </Gap>
      )}
    </NewModal>
  );
};
